import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FontFamily,
  JustifyContent,
  Text,
  TextAlign,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import basketConfig from 'config/basket'
import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'
import type { Pricing } from 'routes/Menu/domains/pricing'
import { usePricing } from 'routes/Menu/domains/pricing'
import { getBasketUsageCount, getNumPortions } from 'selectors/basket'
import { formatPrice } from 'utils/format'

import {
  formatDiscountTip,
  useDiscountDescriptor,
} from '../PriceAndDiscountTip/priceAndDiscountTipUtils'

const getRecipesForPeopleCopy = (numRecipes: number, numPortions: number) => {
  const recipeLabel = numRecipes === 1 ? 'recipe' : 'recipes'
  const peopleLabel = numPortions === 1 ? 'person' : 'people'

  return `${numRecipes} ${recipeLabel} for ${numPortions} ${peopleLabel}`
}

const PriceLine = ({ pricing, isPending }: { pricing?: Pricing | null; isPending: boolean }) => {
  if (isPending) {
    return (
      <Box
        display={Display.Flex}
        justifyContent={[JustifyContent.FlexStart, null, JustifyContent.FlexEnd]}
      >
        <Text color={Color.Success_200} fontFamily={FontFamily.Bold} size={4}>
          £&mdash;
        </Text>
      </Box>
    )
  }

  const grossTotal = parseFloat(pricing?.grossTotal || '0')
  const totalDiscount = parseFloat(pricing?.totalDiscount || '0')
  const total = parseFloat(pricing?.total || '0')

  return totalDiscount > 0 ? (
    <Box
      alignItems={AlignItems.Baseline}
      display={Display.Flex}
      justifyContent={[JustifyContent.FlexStart, null, JustifyContent.FlexEnd]}
    >
      <Text
        data-no-code="boxSummaryPriceOriginal"
        color={Color.ColdGrey_200}
        display={Display.Inline}
        size={0}
        style={{ marginRight: '4px', textDecoration: 'line-through' }}
      >
        {formatPrice(grossTotal)}
      </Text>
      <Text
        data-no-code="boxSummaryPriceDiscounted"
        color={Color.Success_200}
        display={Display.Inline}
        fontFamily={FontFamily.Bold}
        size={4}
      >
        {formatPrice(total)}
      </Text>
    </Box>
  ) : (
    <Box
      display={Display.Flex}
      justifyContent={[JustifyContent.FlexStart, null, JustifyContent.FlexEnd]}
    >
      <Text
        data-no-code="boxSummaryPriceDiscounted"
        color={Color.Success_200}
        display={Display.Inline}
        fontFamily={FontFamily.Bold}
        size={4}
      >
        {formatPrice(grossTotal)}
      </Text>
    </Box>
  )
}

const BasketComprehensionDiscountSummary = ({
  numPortions,
  numRecipes,
}: {
  numPortions: number
  numRecipes: number
}) => {
  const discountTip = formatDiscountTip(useDiscountDescriptor())

  return (
    <Box data-testid="basket-comprehension-summary">
      {discountTip && (
        <Text
          color={Color.Success_200}
          display={Display.Block}
          fontFamily={FontFamily.Bold}
          size={2}
          textAlign={[TextAlign.Left, null, TextAlign.Right]}
        >
          {discountTip} off
        </Text>
      )}
      <Text
        color={Color.White}
        display={Display.Block}
        fontFamily={FontFamily.Bold}
        size={1}
        textAlign={[TextAlign.Left, null, TextAlign.Right]}
      >
        {numRecipes > 0 ? getRecipesForPeopleCopy(numRecipes, numPortions) : '+ FREE delivery'}
      </Text>
    </Box>
  )
}

export const BasketComprehensionSummary = () => {
  const numRecipes = useSelector(getBasketUsageCount)
  const numPortions = useSelector(getNumPortions)
  const { pricing, isPending } = usePricing()
  const isNumRecipesOverBasketLimit = useNumRecipesOverBasketLimit()
  const canCheckout = numRecipes >= basketConfig.minRecipesNum && !isNumRecipesOverBasketLimit

  if (canCheckout && (isPending || pricing?.grossTotal)) {
    return (
      <Box data-testid="basket-comprehension-summary">
        <PriceLine pricing={pricing} isPending={isPending} />
        <Text
          color={Color.White}
          display={Display.Block}
          fontFamily={FontFamily.Bold}
          size={1}
          textAlign={[TextAlign.Left, null, TextAlign.Right]}
        >
          {getRecipesForPeopleCopy(numRecipes, numPortions)}
        </Text>
      </Box>
    )
  }

  return <BasketComprehensionDiscountSummary numPortions={numPortions} numRecipes={numRecipes} />
}
